// ────────────────────────────────────────────────────────────────────────
// CasualShirtsQuestionnaire — Iter 129h.
// Replaces the old "Short-Sleeve Polos" + "Golf Shirts" subcategories with
// a single "Casual Shirts" tile that opens this modal. The tailor picks
// (1) style, (2) shirt colour, (3) pant colour, (4) shoe style + colour.
// The submit callback returns a combo object shaped exactly like the
// SHIRT_SUBCATEGORIES entries so Dashboard can push it straight into the
// existing generateComboLooks() flow — no new backend endpoint required.
// ────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Sparkles } from 'lucide-react';
import { PANT_COLORS as _PANT_COLORS_RAW } from './CustomizeModal';
import TrouserRestructureBlock, {
  buildTrouserStyleDescription,
  buildTrouserStyleTokens,
} from './TrouserRestructureBlock';

// CustomizeModal exports pants as { value: hex, name }. Normalise to
// the { id, hex, label } shape the rest of this component expects.
const PANT_COLOURS = _PANT_COLORS_RAW.map(p => ({ id: p.name, hex: p.value, label: p.name }));

// ── Style catalogue (9 total) ──────────────────────────────────────
// Every entry becomes a `fabric_note` that fully describes the garment
// to Gemini so the render matches the tailor's brief exactly. Each
// note includes:
//   · the specific style (collar / placket / cuff / hem construction)
//   · TUCKED IN — every casual-shirt render must be tucked
//   · "NO jacket" — the shirt is the hero of the shot
//   · "smart-casual, editorial magazine aesthetic"
// The `{shirt}`, `{shirtHex}`, `{pant}`, `{pantHex}`, `{shoe}`,
// `{shoeColour}` placeholders are filled in at submit time.
const STYLES = [
  {
    id: 'classic_polo',
    label: 'Classic Polo',
    desc: 'Short-sleeve knit polo · clean 2-button placket',
    build: ({ shirt, pant, shoe, shoeColour, pantHex }) => (
      `plain ${shirt.toLowerCase()} cotton-pique SHORT-SLEEVE polo — clean two-button placket (NO contrast tipping, NO logo), plain knit collar in the SAME ${shirt.toLowerCase()} as the body, ribbed short sleeves ending mid-bicep, slim fit, TUCKED INTO ${pant.toLowerCase()} cotton chinos (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes, NO jacket. Smart-casual, editorial magazine aesthetic. Never long-sleeve, never untucked.`
    ),
  },
  {
    id: 'angled_placket_pickstitch',
    label: 'Angled Placket Polo (Pickstitch)',
    desc: 'Angled 3-button placket · decorative pickstitch',
    build: ({ shirt, pant, shoe, shoeColour, pantHex }) => (
      `refined ${shirt.toLowerCase()} cotton-pique SHORT-SLEEVE polo with an ANGLED three-button placket (buttons offset on a diagonal, NOT vertical) and visible tonal PICKSTITCH detailing running along the placket edge and collar seam, plain knit collar in the SAME ${shirt.toLowerCase()} as the body, ribbed short sleeves, slim fit, TUCKED INTO ${pant.toLowerCase()} chinos (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes, NO jacket. Smart-casual, editorial magazine aesthetic. Never untucked.`
    ),
  },
  {
    id: 'flat_knit_collar',
    label: 'Flat Knit Collar Polo',
    desc: 'Flat knit collar · Milan-woven ribbed edge',
    build: ({ shirt, pant, shoe, shoeColour, pantHex }) => (
      `luxury ${shirt.toLowerCase()} fine-gauge cotton SHORT-SLEEVE polo with a FLAT KNIT COLLAR (Milan-woven flat rib finish, sitting perfectly flat and low against the body, NOT a rolled pique collar), clean two-button placket in the SAME ${shirt.toLowerCase()} as the body, ribbed short sleeves, tailored fit, TUCKED INTO ${pant.toLowerCase()} chinos (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes, NO jacket. Smart-casual, editorial magazine aesthetic.`
    ),
  },
  {
    id: 'johnny_collar',
    label: 'Johnny Collar Polo',
    desc: 'Buttonless open V placket · sleek Italian silhouette',
    build: ({ shirt, pant, shoe, shoeColour, pantHex }) => (
      `sleek ${shirt.toLowerCase()} fine-gauge cotton SHORT-SLEEVE polo with a JOHNNY COLLAR — small notched V-shaped open-neck placket, ABSOLUTELY NO buttons and NO placket band, the neckline opens directly into a shallow V that shows a clean chest line, plain knit collar in the SAME ${shirt.toLowerCase()} as the body, ribbed short sleeves, TUCKED INTO ${pant.toLowerCase()} chinos (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes, NO jacket. Smart-casual, Italian editorial aesthetic.`
    ),
  },
  {
    id: 'long_sleeve_polo',
    label: 'Long-Sleeve Polo',
    desc: 'Fine-gauge knit · long sleeve · smart casual',
    build: ({ shirt, pant, shoe, shoeColour, pantHex }) => (
      `refined ${shirt.toLowerCase()} fine-gauge cotton LONG-SLEEVE polo with a plain knit collar in the SAME ${shirt.toLowerCase()} as the body, clean two-button placket, ribbed cuffs at the wrist, tailored fit, TUCKED INTO ${pant.toLowerCase()} chinos (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes, NO jacket. Smart-casual, editorial magazine aesthetic. Never untucked.`
    ),
  },
  {
    id: 'ribbed_cuff_polo',
    label: 'Ribbed Cuff Polo',
    desc: 'Classic polo · ribbed sleeve cuffs',
    build: ({ shirt, pant, shoe, shoeColour, pantHex }) => (
      `classic ${shirt.toLowerCase()} cotton-pique SHORT-SLEEVE polo with distinctly RIBBED SLEEVE CUFFS (banded rib knit at the sleeve hem, visibly ribbed and hugging the bicep), plain knit collar in the SAME ${shirt.toLowerCase()} as the body, clean two-button placket, slim fit, TUCKED INTO ${pant.toLowerCase()} chinos (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes, NO jacket. Smart-casual, editorial magazine aesthetic.`
    ),
  },
  {
    id: 'classic_crew_tee',
    label: 'Classic Crew Tee',
    desc: 'Ribbed crew neck · premium cotton',
    build: ({ shirt, pant, shoe, shoeColour, pantHex }) => (
      `premium ${shirt.toLowerCase()} heavyweight combed-cotton SHORT-SLEEVE crew-neck T-SHIRT with a RIBBED CREW NECK (clean tonal ribbed collar band, NOT a polo collar), straight short sleeves, plain hem, tailored fit, TUCKED INTO ${pant.toLowerCase()} chinos (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes, NO jacket. Smart-casual, editorial magazine aesthetic. Never untucked.`
    ),
  },
  {
    id: 'v_neck_tee',
    label: 'V-Neck Tee',
    desc: 'Clean V collar · fine-gauge cotton',
    build: ({ shirt, pant, shoe, shoeColour, pantHex }) => (
      `premium ${shirt.toLowerCase()} fine-gauge cotton SHORT-SLEEVE T-SHIRT with a shallow V-NECK COLLAR (clean V-shaped neckline, NOT crew, NOT scoop), straight short sleeves, plain hem, tailored fit, TUCKED INTO ${pant.toLowerCase()} chinos (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes, NO jacket. Smart-casual, editorial magazine aesthetic.`
    ),
  },
  {
    id: 'ribbed_hem_tee',
    label: 'Tee with Ribbed Sleeve Hems',
    desc: 'Crew neck · banded rib sleeve hems',
    build: ({ shirt, pant, shoe, shoeColour, pantHex }) => (
      `premium ${shirt.toLowerCase()} heavyweight cotton SHORT-SLEEVE T-SHIRT with a clean crew neck and distinctly RIBBED SLEEVE HEMS (visible tonal rib knit band at the end of each sleeve, hugging the bicep), tailored fit, TUCKED INTO ${pant.toLowerCase()} chinos (${pantHex}) with brown leather belt, ${shoeColour.toLowerCase()} ${shoe} shoes, NO jacket. Smart-casual, editorial magazine aesthetic.`
    ),
  },
];

// Reuse the Custom Fabric palettes (single source of truth) so every
// casual-shirt render lives in the same colour vocabulary as the rest
// of the app.
const SHIRT_COLOURS = [
  { id: 'White',          label: 'White',          hex: '#FFFFFF' },
  { id: 'Navy',           label: 'Navy',           hex: '#1C2841' },
  { id: 'Black',          label: 'Black',          hex: '#0B0B0B' },
  { id: 'Light Blue',     label: 'Light Blue',     hex: '#BCD4E6' },
  { id: 'Sage Green',     label: 'Sage Green',     hex: '#A7C8A5' },
  { id: 'Cream',          label: 'Cream',          hex: '#F5F0E1' },
  { id: 'Heather Grey',   label: 'Heather Grey',   hex: '#9CA3AF' },
  { id: 'Burgundy',       label: 'Burgundy',       hex: '#6E1F1F' },
  { id: 'Olive',          label: 'Olive',          hex: '#556B2F' },
];

const SHOE_STYLES = [
  { id: 'loafer',       label: 'Loafer' },
  { id: 'derby',        label: 'Derby' },
  { id: 'oxford',       label: 'Oxford' },
  { id: 'sneaker',      label: 'Sneaker' },
  { id: 'chelsea_boot', label: 'Chelsea Boot' },
  { id: 'monk',         label: 'Monk-Strap' },
];

const SHOE_COLOURS = [
  { id: 'Black',         label: 'Black',         hex: '#0A0A0C' },
  { id: 'Dark Brown',    label: 'Dark Brown',    hex: '#3A2418' },
  { id: 'Cognac',        label: 'Cognac',        hex: '#9B5E2E' },
  { id: 'Tan',           label: 'Tan',           hex: '#C19A6B' },
  { id: 'White',         label: 'White',         hex: '#F5F0E1' },
  { id: 'Oxblood',       label: 'Oxblood',       hex: '#582430' },
];

const _Section = ({ title, children, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    <h4 className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] font-semibold">
      {title}
    </h4>
    {children}
  </div>
);

const _Chip = ({ selected, onClick, hex, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 border text-[11px] uppercase tracking-wider transition-all ${selected
      ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[#F5F5F5]'
      : 'border-[#27272A] text-[#A1A1AA] hover:border-[#3F3F46]'}`}
    data-testid={testid}
  >
    {hex && <span className="w-4 h-4 border border-[#27272A]" style={{ backgroundColor: hex }} />}
    <span>{label}</span>
  </button>
);

export default function CasualShirtsQuestionnaire({
  open, onClose, onSubmit,
  generating = false,
  hasClientImage = true,
}) {
  const [styleId,    setStyleId]    = useState(STYLES[0].id);
  const [shirtId,    setShirtId]    = useState(SHIRT_COLOURS[0].id);
  const [pantId,     setPantId]     = useState(PANT_COLOURS[0]?.id || 'Navy');
  const [shoeStyle,  setShoeStyle]  = useState('loafer');
  const [shoeColour, setShoeColour] = useState('Cognac');
  // Iter 169 — trouser restructure state (pleats/cuffs/waistband/belt).
  // Same shape as CustomFabricQuestionnaire so all popouts share one
  // vocabulary and one visual block.
  const [trPleats,     setTrPleats]     = useState(null);
  const [trCuffs,      setTrCuffs]      = useState(false);
  const [trWaist,      setTrWaist]      = useState(null);
  const [trBeltColor,  setTrBeltColor]  = useState(null);

  const handleSubmit = () => {
    const style   = STYLES.find(s => s.id === styleId) || STYLES[0];
    const shirt   = SHIRT_COLOURS.find(c => c.id === shirtId) || SHIRT_COLOURS[0];
    const pant    = PANT_COLOURS.find(c => c.id === pantId) || { id: 'Navy', hex: '#1C2841', label: 'Navy' };
    const shoe    = SHOE_STYLES.find(s => s.id === shoeStyle) || SHOE_STYLES[0];
    const shoeCol = SHOE_COLOURS.find(c => c.id === shoeColour) || SHOE_COLOURS[0];

    const fabric_note = style.build({
      shirt:      shirt.label,
      shirtHex:   shirt.hex,
      pant:       pant.label,
      pantHex:    pant.hex,
      shoe:       shoe.label.toLowerCase(),
      shoeColour: shoeCol.label,
    }) + (
      // Iter 147 — belt-and-braces layering lock. Gemini occasionally
      // added an unrequested VEST over tees/polos despite the "NO
      // jacket" wording. Every casual-shirt fabric_note now spells out
      // the full forbidden-layer list; the backend additionally appends
      // a final-authority override at the end of the prompt.
      ' WORN ALONE — the shirt is the ONLY top-half garment: absolutely NO vest,'
      + ' NO waistcoat, NO gilet, NO cardigan, NO sweater, NO overshirt, NO jacket,'
      + ' NO blazer, and NO layer of any kind over or under the shirt.'
    ) + (
      // Iter 169 — trouser restructure sentence appended to the fabric
      // note so Gemini honours the pleats / cuffs / waistband / belt
      // colour the tailor picked. Empty string when nothing selected.
      buildTrouserStyleDescription({ trPleats, trCuffs, trWaist, trBeltColor })
    );

    onSubmit?.({
      combo: {
        // `garment` is used by the backend as the per-slot dedup key
        // (see /generate-look `dedup_filter`). We include the shirt
        // COLOUR in the garment name so a White Classic Polo and a
        // Navy Classic Polo don't collide and overwrite each other.
        garment: `${shirt.label} ${style.label}`,
        garment_id: 'casual_shirt',
        style_id: style.id,
        // `color` also feeds the dedup filter — passing the shirt hex
        // gives us belt-and-braces uniqueness in case two combos share
        // the same garment name but differ in another dimension.
        color: shirt.label,
        suit: shirt.hex,
        pattern: 'solid',
        shirt: shirt.label,
        pant: pant.hex,
        shoe: shoe.id,
        shoeColor: shoeCol.label,
        label: `${shirt.label} ${style.label}`,
        fabric_note,
        // Iter 233 (Feb 2026) — send the composite `trouser_style`
        // token so the backend's structural-only lock enforces the
        // waistband choice (side_tabs REPLACES belt loops), not just
        // the fabric_note prose. Without this the "Side tabs"
        // waistband was picked in the UI but the render kept
        // rendering belt loops because only the fabric_note carried
        // the signal and Gemini prefers the structural lock.
        trouser_style: buildTrouserStyleTokens({ trPleats, trCuffs, trWaist, trBeltColor }) || undefined,
      },
      category: 'shirts_casual_shirts',
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0A0A0C] border-[#27272A] rounded-none"
        data-testid="casual-shirts-questionnaire-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Casual Shirt Builder
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A1A1AA]">
            Pick a style, colours and shoes — we&apos;ll render one editorial look with your client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* ── Style ─────────────────────────────────────────── */}
          <_Section title="Style" testid="casual-shirts-style-section">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {STYLES.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStyleId(s.id)}
                  className={`text-left px-3 py-3 border transition-all ${styleId === s.id
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10'
                    : 'border-[#27272A] hover:border-[#3F3F46]'}`}
                  data-testid={`casual-shirts-style-${s.id}`}
                >
                  <p className="text-sm text-[#F5F5F5]">{s.label}</p>
                  <p className="text-[10px] text-[#A1A1AA] mt-0.5">{s.desc}</p>
                </button>
              ))}
            </div>
          </_Section>

          {/* ── Shirt colour ─────────────────────────────────── */}
          <_Section title="Shirt Colour" testid="casual-shirts-color-section">
            <div className="flex flex-wrap gap-2">
              {SHIRT_COLOURS.map(c => (
                <_Chip
                  key={c.id}
                  selected={shirtId === c.id}
                  onClick={() => setShirtId(c.id)}
                  hex={c.hex}
                  label={c.label}
                  testid={`casual-shirts-color-${c.id.replace(/\s+/g, '-').toLowerCase()}`}
                />
              ))}
            </div>
          </_Section>

          {/* ── Pant colour ──────────────────────────────────── */}
          <_Section title="Trouser Colour" testid="casual-shirts-pant-section">
            <div className="flex flex-wrap gap-2">
              {PANT_COLOURS.map(c => (
                <_Chip
                  key={c.id}
                  selected={pantId === c.id}
                  onClick={() => setPantId(c.id)}
                  hex={c.hex}
                  label={c.label}
                  testid={`casual-shirts-pant-${c.id.replace(/\s+/g, '-').toLowerCase()}`}
                />
              ))}
            </div>
          </_Section>

          {/* ── Trouser Restructure (iter 169) ───────────────── */}
          <_Section title="Trouser Restructure" testid="casual-shirts-trouser-restructure-section">
            <TrouserRestructureBlock
              trPleats={trPleats}       setTrPleats={setTrPleats}
              trCuffs={trCuffs}         setTrCuffs={setTrCuffs}
              trWaist={trWaist}         setTrWaist={setTrWaist}
              trBeltColor={trBeltColor} setTrBeltColor={setTrBeltColor}
              testIdPrefix="casual-shirts-trouser"
            />
          </_Section>

          {/* ── Shoes ────────────────────────────────────────── */}
          <_Section title="Shoes" testid="casual-shirts-shoe-section">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-[#52525B]">Style</p>
                <div className="flex flex-wrap gap-2">
                  {SHOE_STYLES.map(s => (
                    <_Chip
                      key={s.id}
                      selected={shoeStyle === s.id}
                      onClick={() => setShoeStyle(s.id)}
                      label={s.label}
                      testid={`casual-shirts-shoe-style-${s.id}`}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider text-[#52525B]">Colour</p>
                <div className="flex flex-wrap gap-2">
                  {SHOE_COLOURS.map(c => (
                    <_Chip
                      key={c.id}
                      selected={shoeColour === c.id}
                      onClick={() => setShoeColour(c.id)}
                      hex={c.hex}
                      label={c.label}
                      testid={`casual-shirts-shoe-color-${c.id.replace(/\s+/g, '-').toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </_Section>

          {/* ── Submit ───────────────────────────────────────── */}
          <div className="pt-4 border-t border-[#27272A] flex flex-col gap-3">
            {!hasClientImage && (
              <div
                className="px-4 py-3 border border-[#4F2F1F] bg-[#2A1A0F] flex items-start gap-3"
                data-testid="casual-shirts-no-client-photo-warning"
              >
                <div className="text-[#D4A87A] text-sm mt-0.5">⚠</div>
                <div className="flex-1">
                  <p className="text-[11px] uppercase tracking-widest text-[#D4A87A] font-semibold">
                    Client photo required
                  </p>
                  <p className="text-[11px] text-[#A1A1AA] mt-1 leading-relaxed">
                    The render needs the client&apos;s face reference. Close this modal and upload a photo in the <em>Client Photo</em> panel, then click Render again.
                  </p>
                </div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={generating}
                className="text-[#A1A1AA] hover:bg-[#1A1A1E] rounded-none"
                data-testid="casual-shirts-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={generating || !hasClientImage}
                title={!hasClientImage ? 'Upload a client photo first — the render needs a face reference' : undefined}
                className="rounded-none uppercase tracking-wider text-xs px-6 py-5 disabled:opacity-40"
                style={{ backgroundColor: 'hsl(var(--accent))', color: '#0A0A0C' }}
                data-testid="casual-shirts-submit"
              >
                {generating
                  ? (<><Loader2 className="w-4 h-4 spinner mr-2" /> Rendering…</>)
                  : (<><Sparkles className="w-4 h-4 mr-2" /> Render Look</>)}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
